import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { InlineAlertType, Query } from '@varo/graphql-schema-types';
import { setStringAsync } from 'expo-clipboard';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { Option } from 'features/directDeposit/types/Option';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import FetchDirectDepositAccountInfo from 'services/graphql/queries/MoveMoney/DirectDepositAccountInfo';
import FDICDisclaimer from 'shared/components/FDICDisclaimer/FDICDisclaimer';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import {
  LAUNCH_VARO_DD_FORM,
  PINWHEEL_LAUNCH_DEEP_LINK,
  PINWHEEL_LAUNCH_ERROR,
} from 'shared/constants/pinWheel';
import { isIOS, isWeb } from 'shared/constants/platform';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useDirectDepositManagementFlow from 'shared/hooks/pinwheel/useDirectDepositManagementFlow/useDirectDepositManagementFlow';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import { useAutoHideSnackbar } from 'shared/hooks/snackBar/useAutoHideSnackbar';
import useLayoutReady from 'shared/hooks/useLayoutReady/useLayoutReady';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { useQuery } from 'urql';

const Separator = () => {
  return (
    <View style={styles.dividerWrapper}>
      <HairLineDivider />
    </View>
  );
};

const DirectDepositScreen = () => {
  const router = useRouter();
  const { name } = useLocalSearchParams<{
    name?: string;
  }>();

  const bottomSheetRef = useRef<BottomSheetModal>(null);

  const isLayoutReady = useLayoutReady();
  const insets = useSafeAreaInsets();

  const { t: translatedText } = useTranslation('directDeposit');
  const { t: translatedTextOptions } = useTranslation('directDeposit', {
    keyPrefix: 'options',
  });

  const [{ data: accountData, fetching }] = useQuery<Query>({
    query: FetchDirectDepositAccountInfo,
    requestPolicy: 'cache-and-network',
  });

  const [showSnackBar, setShowSnackBar] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState(
    translatedText('copied'),
  );
  const [isPinwheelDeepLinkInitiated, setIsPinwheelDeepLinkInitiated] =
    useState(false);

  const { initiatePinwheel, isPinwheelLoading } = usePinwheel();

  const { refetchDdManagementFlow } = useDirectDepositManagementFlow({
    pause: true,
  });

  const hasPinwheelLaunchError = name === PINWHEEL_LAUNCH_ERROR;

  useViewDidFocusScreenAnalyticsEvent(
    hasPinwheelLaunchError
      ? {
          key: 'ddManagementFallbackError',
        }
      : null,
  );

  useAutoHideSnackbar(Boolean(showSnackBar), () => {
    setShowSnackBar(false);
  });

  const showAccountInfoSheet = () => {
    bottomSheetRef.current?.present();
  };

  useEffect(() => {
    const handlePopstate = () => {
      bottomSheetRef.current?.dismiss();
    };

    if (isWeb) {
      window.addEventListener('popstate', handlePopstate);
      return () => {
        window.removeEventListener('popstate', handlePopstate);
      };
    }
  }, [bottomSheetRef]);

  const handleSendHrForm = () => {
    router.navigate({
      pathname: '/move-money/direct-deposit/direct-deposit-form',
      params: {
        id: accountData?.myAccounts?.forDirectDepositSetup[0]?.id,
      },
    });
  };

  const options: Option[] = [
    {
      title: translatedTextOptions('setupAutomatically.title'),
      description: translatedTextOptions('setupAutomatically.description'),
      icon: 'TOGGLE_A',
      onPress: () => initiatePinwheel({ sourceName: 'direct_deposit_menu' }),
      analyticsId: 'directDepositInstantSwitchInitiated',
      isDisabled: hasPinwheelLaunchError,
    },
    {
      title: translatedTextOptions('sendHrForm.title'),
      description: translatedTextOptions('sendHrForm.description'),
      icon: 'SIGN_B',
      onPress: () => void handleSendHrForm(),
      analyticsId: hasPinwheelLaunchError
        ? 'formSubmissionClicked'
        : 'depositFormAccessed',
      customAttributes: hasPinwheelLaunchError
        ? null
        : { cta: 'Send HR a direct deposit form' },
    },
    {
      title: translatedTextOptions('copyPasteBankingInfo.title'),
      description: translatedTextOptions('copyPasteBankingInfo.description'),
      icon: 'BASICS_A',
      onPress: showAccountInfoSheet,
      analyticsId: hasPinwheelLaunchError
        ? 'bankCopyPasteClicked'
        : 'accountNumbersClicked',
    },
  ];

  const copyToClipboard = useCallback(
    (text: string | null | undefined) => {
      if (text) {
        void setStringAsync(text);
        setSnackBarMessage(translatedText('copied'));
      } else {
        setSnackBarMessage(translatedText('exceptionalError'));
      }
      bottomSheetRef.current?.dismiss();
      setShowSnackBar(true);
    },
    [translatedText],
  );

  useEffect(() => {
    if (name === PINWHEEL_LAUNCH_DEEP_LINK && !isPinwheelDeepLinkInitiated) {
      setIsPinwheelDeepLinkInitiated(true);
      initiatePinwheel({
        onOutcome: refetchDdManagementFlow,
      });
    }
  }, [
    refetchDdManagementFlow,
    initiatePinwheel,
    isPinwheelDeepLinkInitiated,
    name,
  ]);

  useEffect(() => {
    if (!isLayoutReady) {
      return;
    }
    if (
      name === LAUNCH_VARO_DD_FORM &&
      accountData?.myAccounts?.forDirectDepositSetup?.[0]?.id
    ) {
      router.navigate({
        pathname: '/move-money/direct-deposit/direct-deposit-form',
        params: {
          id: accountData.myAccounts.forDirectDepositSetup[0].id,
        },
      });
    }
  }, [
    accountData?.myAccounts?.forDirectDepositSetup,
    isLayoutReady,
    name,
    router,
  ]);

  return (
    <View
      style={[
        styles.container,
        {
          paddingBottom: isIOS
            ? insets.bottom
            : insets.bottom + theme.spacing.m,
        },
      ]}
    >
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
          title: translatedText('header') ?? '',
        }}
      />
      {hasPinwheelLaunchError ? (
        <View style={styles.subHeaderContainer}>
          <InlineAlert
            alertType={InlineAlertType.Info}
            bodyText={translatedText('pinwheelLaunchErrorAlertTitle')}
          />
        </View>
      ) : null}
      <View style={styles.subHeaderContainer}>
        <TitleLgText>{translatedText('subHeader')}</TitleLgText>
      </View>
      <View>
        <FlatList
          ListHeaderComponent={() => (
            <SectionDivider labelText={translatedText('dividerTitle')} />
          )}
          data={options}
          keyExtractor={(item) => item.title}
          renderItem={({ item }) => (
            <ListItem
              primaryCopy={item.title}
              secondaryCopy={item.description}
              onPress={item.onPress}
              leadingAccessory={{
                id: item.title,
                name: VXLComponentId.ICON_AVATAR,
                props: {
                  variant: 'LG',
                  illustrationKey: item.icon,
                },
              }}
              analyticsId={item.analyticsId}
              customAttributes={item.customAttributes}
              isDisabled={!!item.isDisabled}
            />
          )}
          ItemSeparatorComponent={() => <Separator />}
          removeClippedSubviews={false}
        />
      </View>
      <BottomSheet ref={bottomSheetRef}>
        {fetching ? (
          <View style={styles.bottomSheetLoaderIndicator}>
            <LoadingSpinner />
          </View>
        ) : (
          <>
            <View style={styles.bottomSheetHeaderWrapper}>
              <TitleMdText>
                {translatedText('options.copyAccountTitle')}
              </TitleMdText>
              <BodyBaseText>
                {translatedText('options.copyAccountSubTitle')}
              </BodyBaseText>
            </View>
            <ListItem
              lineFormat={ListItemLineFormat.LABEL}
              secondaryCopy={translatedText('options.copyAccountAccountNumber')}
              primaryCopy={
                (accountData?.myAccounts?.forDirectDepositSetup?.[0]
                  ?.fullAccountNumber as string) ?? ''
              }
              onPress={() => {
                const AccountNumberToCopy =
                  (accountData?.myAccounts?.forDirectDepositSetup?.[0]
                    ?.fullAccountNumber as string) ?? '';
                copyToClipboard(AccountNumberToCopy);
              }}
              trailingAccessory={{
                id: '1',
                name: VXLComponentId.ICON,
                props: {
                  iconKey: 'COPY',
                  iconAccessibilityLabel: 'accountNumber-copy-icon',
                },
              }}
              showsChevron={false}
              analyticsId="accountNumberCopied"
            />
            <Separator />
            <ListItem
              lineFormat={ListItemLineFormat.LABEL}
              secondaryCopy={translatedText('options.copyAccountRouting')}
              primaryCopy={
                accountData?.myAccounts?.forDirectDepositSetup?.[0]
                  ?.routingNumber ?? ''
              }
              trailingAccessory={{
                id: '1',
                name: VXLComponentId.ICON,
                props: {
                  iconKey: 'COPY',
                  iconAccessibilityLabel: 'routingNumber-copy-icon',
                },
              }}
              onPress={() => {
                const routingNumberToCopy =
                  (accountData?.myAccounts?.forDirectDepositSetup?.[0]
                    ?.routingNumber as string) ?? '';
                copyToClipboard(routingNumberToCopy);
              }}
              showsChevron={false}
              analyticsId="routingNumberCopied"
            />
          </>
        )}
      </BottomSheet>
      {showSnackBar ? (
        <Snackbar
          text={snackBarMessage}
          buttonOnPress={() => setShowSnackBar(false)}
        />
      ) : null}
      <ScreenTransitionOverlay
        isLoading={isPinwheelLoading || fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={null}
      />
      <View style={styles.disclosureContainer}>
        <BodyBaseText style={styles.disclosureText}>
          {translatedText('disclosure')}
        </BodyBaseText>
      </View>
      <FDICDisclaimer />
    </View>
  );
};

const styles = StyleSheet.create({
  bottomSheetHeaderWrapper: {
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
  bottomSheetLoaderIndicator: {
    alignItems: 'center',
    height: 300,
    justifyContent: 'center',
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flexGrow: 1,
  },
  disclosureContainer: {
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  disclosureText: {
    color: theme.color.text.secondary,
  },
  dividerWrapper: {
    paddingHorizontal: theme.spacing.m,
  },
  subHeaderContainer: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
});

export default DirectDepositScreen;
